// src/contexts/OrderContext.js
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../utilities/api';
import { utcToLocal, utcStringToDate } from '../utilities/utilities';
import { calculateBMI } from '../utilities/weightCalculations';
import {
  clearPaymentAction,
  getPaymentAction,
  paymentActionMatchesOrder,
} from '../utilities/paymentAction';
import { useUser } from './UserContext';

const OrderContext = createContext();
export const useOrder = () => useContext(OrderContext);

export const OrderProvider = ({ children }) => {
  const { isLoggedIn, user } = useUser();
  const [orders, setOrders] = useState([]);
  // Initialize currentOrder from localStorage if available
  const [currentOrder, setCurrentOrder] = useState(() => {
    const savedOrder = localStorage.getItem('redimdwm_current_order');
    if (!savedOrder) return {};

    try {
      return JSON.parse(savedOrder);
    } catch {
      localStorage.removeItem('redimdwm_current_order');
      return {};
    }
  });
  const [orderHistory, setOrderHistory] = useState([]);
  const [pricingData, setPricingData] = useState([]);
  // The full, authenticated product list (remote_get_pricing_info) — unlike
  // pricingData (remote_get_public_pricing_info, is_public=1 only), this
  // includes every product a patient might hold an order for, plus the
  // signatura facets (route, dispense_unit, dispense_qty_per_month, etc.)
  // that the `orders` rows themselves don't carry. Any screen that needs a
  // product's facets by product_code looks it up here.
  const [products, setProducts] = useState([]);
  const [isProductsLoading, setIsProductsLoading] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [isOrderHistoryLoading, setIsOrderHistoryLoading] = useState(false);
  const [isPricingLoading, setIsPricingLoading] = useState(false);
  const [isActionOrderLoading, setIsActionOrderLoading] = useState(false);
  const [actionPayer, setActionPayer] = useState(null);
  const [actionPaymentComplete, setActionPaymentComplete] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  const [isMutatingOrder, setIsMutatingOrder] = useState(false);
  const [error, setError] = useState(null);

  // Lookup a single order by ID
  const getOrderById = useCallback(
    orderId => orders.find(o => o.order_id === orderId),
    [orders]
  );


  // Fetch & sort active orders
  const refreshOrders = useCallback(async () => {
    setIsOrdersLoading(true);
    try {
      const res = await api.getOrders();

      if (!res.error && Array.isArray(res.data)) {
        const sorted = res.data
          .map(o => ({ ...o, order_date: utcToLocal(utcStringToDate(o.order_date)) }))
          .sort((a, b) => new Date(b.order_date) - new Date(a.order_date));

        setOrders(sorted);
        setError(null);
      } else {
        setError(res.error || 'Failed to fetch orders');
      }
    } catch {
      setError('Failed to fetch orders');
    } finally {
      setIsOrdersLoading(false);
    }
  }, []);

  // Fetch & sort order history
  const refreshOrderHistory = useCallback(async () => {
    setIsOrderHistoryLoading(true);
    try {
      const res = await api.getOrderHistory();
      if (!res.error && Array.isArray(res.data)) {
        const sorted = res.data
          .map(o => ({ ...o, reported_date: utcToLocal(utcStringToDate(o.reported_date)) }))
          .sort((a, b) => new Date(b.reported_date) - new Date(a.reported_date));
        setOrderHistory(sorted);
        setError(null);
      } else {
        setError(res.error || 'Failed to fetch order history');
      }
    } catch {
      setError('Failed to fetch order history');
    } finally {
      setIsOrderHistoryLoading(false);
    }
  }, []);
  
  // Wrap setCurrentOrder to persist to localStorage
  const persistCurrentOrder = useCallback((orderData) => {
    // Save to localStorage for persistence across page refreshes
    if (orderData && Object.keys(orderData).length > 0) {
      localStorage.setItem('redimdwm_current_order', JSON.stringify(orderData));
    } else {
      // If empty object, remove from localStorage
      localStorage.removeItem('redimdwm_current_order');
    }
    setCurrentOrder(orderData);
  }, []);

  const setActionOrder = useCallback((orderData) => {
    // Action-link data must not enter the normal localStorage order cache.
    localStorage.removeItem('redimdwm_current_order');
    setCurrentOrder(orderData || {});
  }, []);

  const loadActionPayment = useCallback(async () => {
    const action = getPaymentAction();
    if (!action) {
      setActionOrder({});
      setActionPayer(null);
      setActionPaymentComplete(false);
      return { success: false, error: 'Unable to open this payment link.' };
    }

    setActionOrder({});
    setActionPayer(null);
    setActionPaymentComplete(false);
    setIsActionOrderLoading(true);
    try {
      const response = await api.getOrderDetailWithAction(action.token, action.orderId);
      const order = response?.data;
      const payer = response?.payer;
      if (
        !response?.success
        || !paymentActionMatchesOrder(action, order?.order_id)
        || Number(payer?.userid) !== Number(action.userId)
      ) {
        clearPaymentAction();
        setActionOrder({});
        setActionPayer(null);
        setActionPaymentComplete(false);
        return { success: false, error: response?.error || 'Unable to open this payment link.' };
      }

      setActionOrder(order);
      setActionPayer(payer);
      setError(null);
      return { success: true, order, payer, action };
    } catch {
      return { success: false, error: 'Unable to open this payment link.' };
    } finally {
      setIsActionOrderLoading(false);
    }
  }, [setActionOrder]);
  
 const setOrder = useCallback((orderId, orderData = null) => {
    // If orderData is provided directly (e.g., from a just-created order), use it
    let order = orderData;

    // Otherwise, look it up in the orders array
    if (!order) {
      order = getOrderById(orderId);
    }

    if (!order || String(order.patient_id) !== String(user?.userid)) {
      setError('Order not found or you do not have permission');
      return false;
    }
    // Use the wrapped function that persists to localStorage
    persistCurrentOrder(order);
    return true;
  }, [getOrderById, user, persistCurrentOrder]);

  // load order pricing data
  const refreshPricing = useCallback(async () => {
    setIsPricingLoading(true);
    setPricingData([]);
    setPricingError(null);
    try {
      const response = await api.getPublicPriceInfo();
      if(!response.success) {
        setPricingError(response.error || 'Failed to fetch pricing information');
        throw new Error(response.error || 'Failed to fetch pricing information');
      }
      setPricingData(response.data);
    } catch (err) {
      const msg = err.message || 'Failed to fetch pricing information';
      setPricingError(msg);
    } finally {
      setIsPricingLoading(false);
    }
  }, []);

  // load the full, authenticated product list (all products, including
  // is_public=0 ones — a patient can hold an order for a product that
  // isn't publicly advertised)
  const refreshProducts = useCallback(async () => {
    setIsProductsLoading(true);
    try {
      const response = await api.getPriceInfo();
      if (response?.success && Array.isArray(response.data)) {
        setProducts(response.data);
      }
    } finally {
      setIsProductsLoading(false);
    }
  }, []);

  const getProductByCode = useCallback(
    code => (code == null ? null : products.find(p => String(p.product_code) === String(code)) || null),
    [products]
  );

  // Memoized “latest” and “recent” subsets
  const latestOrder  = useMemo(() => orders[0] || null, [orders]);
  const recentOrders = useMemo(() => orders.slice(0, 3), [orders]);

  // Create a brand-new order, then refresh lists
  const addOrder = useCallback(
    async payload => {
      setIsMutatingOrder(true);
      try {
        const res = await api.addOrder(payload);
        if (res?.success && res.data) {
          await refreshOrders();
          await refreshOrderHistory();
          return { success: true, order: res.data };
        }
        throw new Error(res?.error || 'Failed to create order');
      } catch (err) {
        const msg = err.message || 'Failed to create order';
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setIsMutatingOrder(false);
      }
    },
    [refreshOrders, refreshOrderHistory]
  );

  // Intake form submission → payload → new order
  const submitIntake = useCallback(
    formData => {
      if (!user?.userid) {
        const msg = 'Cannot create order: missing user ID';
        setError(msg);
        return Promise.resolve({ success: false, error: msg });
      }

      // Calculate BMI
      const bmi = calculateBMI(formData.height, formData.weight);

      // Calculate weight lost since previous reading
      let weightLost = null;
      const currentWeight = parseFloat(formData.weight);

      if (!isNaN(currentWeight)) {
        if (orderHistory && orderHistory.length > 0) {
          // Get the most recent weight from order history
          const previousWeight = parseFloat(orderHistory[0]?.weight);
          if (!isNaN(previousWeight)) {
            weightLost = parseFloat((previousWeight - currentWeight).toFixed(1));
          }
        } else if (user?.weight) {
          // If no order history, use user's initial weight
          const initialWeight = parseFloat(user.weight);
          if (!isNaN(initialWeight)) {
            weightLost = parseFloat((initialWeight - currentWeight).toFixed(1));
          }
        }
      }

      const payload = {
        userid: user.userid,
        email: user.email,
        drug:         formData.drug,
        dosage:       formData.dosage,
        product_code: formData.product_code,
        order_qty:    formData.order_qty,
        height:       formData.height,
        weight:       formData.weight,
        goal_weight:  formData.desiredWeight,
        complaint:    formData.complaint,
        order_type:   'refill',
        updated_by:   user.userid,
        bmi:          bmi,
        weightlost:   weightLost,
        dose_request: formData.dose_request,
        abdominal_pain: formData.abdominal_pain,
        abdominal_pain_details: formData.abdominal_pain_details,
        gastrointestinal: formData.gastrointestinal,
        gastrointestinal_details: formData.gastrointestinal_details,
        other_side_effects: formData.other_side_effects,
        other_side_effects_details: formData.other_side_effects_details,
      };

      return addOrder(payload);
    },
    [user, addOrder, orderHistory]
  );

// record paypal payment transaction
const submitPayment = useCallback(
  async (orderId, payPalData, options = {}) => {
    setIsMutatingOrder(true);
    try {
      if (!orderId) {
        throw new Error("Order ID is missing");
      }

      const actionRequired = options.actionMode === true;
      const action = actionRequired ? getPaymentAction() : null;
      const paymentPayload = { ...(payPalData || {}) };

      if (actionRequired && !paymentActionMatchesOrder(action, orderId)) {
        clearPaymentAction();
        setActionPayer(null);
        setActionPaymentComplete(false);
        setActionOrder({});
        throw new Error('Unable to continue this payment.');
      }

      const paymentData = {
        ...paymentPayload,
        order_id: orderId,
        orders: {
          ...(paymentPayload.orders || {}),
          order_id: orderId,
        },
      };

      const res = actionRequired
        ? await api.applyPaymentWithAction(action.token, paymentData)
        : await api.applyPayment(paymentData);

      if (res.success) {
        if (actionRequired) {
          clearPaymentAction();
          setActionOrder({});
          setActionPaymentComplete(true);
        } else {
          await refreshOrders();
          persistCurrentOrder({});
        }
        
        return res;
      } else {
        if (actionRequired && (res?.httpStatus === 401 || res?.httpStatus === 403)) {
          clearPaymentAction();
          setActionPayer(null);
          setActionOrder({});
          setActionPaymentComplete(false);
        }
        throw new Error(res.error || "Failed to process payment");
      }
    } catch (err) {
      const errMsg = `Payment error: ${err.message}`;
      setError(errMsg);
      return { error: errMsg };
    } finally {
      setIsMutatingOrder(false);
    }
  },
  [refreshOrders, persistCurrentOrder, setActionOrder]
);

// FX-179: local bookkeeping ONLY, for a payment the backend has already
// captured and recorded in /cp/orders/remote_capture_paypal_order.
//
// There is nothing to submit — the payments row, the order status and the
// receipt already exist. Posting remote_apply_payment here would be a second
// write for money that is on the books. All that is owed is refreshing this
// browser's view of the order and closing out a payment-link session.
//
// Zero-total ("Free Order") payments are NOT this: they never went to PayPal,
// so they still go through submitPayment/remote_apply_payment above.
const completeRecordedPayment = useCallback(
  async (options = {}) => {
    if (options.actionMode === true) {
      clearPaymentAction();
      setActionOrder({});
      setActionPaymentComplete(true);
      return;
    }
    await refreshOrders();
    persistCurrentOrder({});
  },
  [refreshOrders, persistCurrentOrder, setActionOrder]
);

  // On login, auto-fetch orders/history
  useEffect(() => {
    if (isLoggedIn && user?.userid) {
      refreshOrders();
      refreshOrderHistory();
      refreshPricing();
      refreshProducts();
    }
  }, [isLoggedIn, user?.userid, refreshOrders, refreshOrderHistory, refreshPricing, refreshProducts]);



  const isLoading = isOrdersLoading || isOrderHistoryLoading || isPricingLoading || isProductsLoading || isMutatingOrder || isActionOrderLoading;

  const value = {
    orders,
    currentOrder,
    orderHistory,
    latestOrder,
    recentOrders,
    pricingData,
    products,
    isLoading,
    isOrdersLoading,
    isOrderHistoryLoading,
    isPricingLoading,
    isProductsLoading,
    pricingError,
    actionPayer,
    actionPaymentComplete,
    isActionOrderLoading,
    error,
    setCurrentOrder: persistCurrentOrder,
    setActionOrder,
    setOrder,
    refreshOrders,
    refreshOrderHistory,
    refreshPricing,
    refreshProducts,
    getProductByCode,
    submitIntake,
    submitPayment,
    completeRecordedPayment,
    loadActionPayment,
  };

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};
